/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { DEFAULT_ALERT_COLUMN_IDS, ALERT_ROW_COUNT_OPTIONS } from 'constants';

import React, { useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import {
  Dropdown,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  Button,
  TooltipDefinition,
} from 'carbon-addons-iot-react';
import { ArrowUp16, ArrowDown16 } from '@carbon/icons-react';
import PropTypes from 'prop-types';

import { SortDirection } from 'CommonTypes';

const messages = defineMessages({
  rowsPerPage: 'Rows per page',
  rowHeight: 'Row height',
  normal: 'Normal',
  short: 'Short',
  sortBy: 'Sort by',
  thenBy: 'Then by',
  addSort: 'Add sort',
  tooltipDefinition: 'Link to a device from this column',
  selectAColumn: 'Select a column',
  selectRowCount: 'Select row count',
  selectSortColumn: 'Select sort column',
  ascending: 'Ascending',
  descending: 'Descending',
  drilldownTooltip: 'Adds a link from this column to the device that triggered the alert',
  none: 'None',
  'open.menu': 'Open menu',
  'close.menu': 'Close menu',
});

const propTypes = {
  cardConfig: PropTypes.shape({
    columnsState: PropTypes.arrayOf(PropTypes.shape({})),
    customRowCount: PropTypes.number,
    customRowHeight: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  isSummaryDashboard: PropTypes.bool,
};

const defaultProps = {
  cardConfig: {},
  isSummaryDashboard: false,
};

const AlertCardEditSettings = ({ cardConfig, onChange, isSummaryDashboard }) => {
  const { formatMessage } = useIntl();
  const { columnsState, customRowCount, customRowHeight } = cardConfig;

  // Should only be able to sort on visible columns
  const columnsToSort = columnsState
    .filter(({ isVisible }) => isVisible)
    .map(({ id, columnName }) => ({ id, text: columnName }));

  // The existing "sortBy" column OR default timestamp
  const sortByColumn = columnsState.find(({ sortDirection }) => sortDirection) || {
    id: 'timestamp',
    columnName: 'Time',
    direction: SortDirection.DESC,
  };

  // Should only be able to create a drilldown for custom columns
  const drilldownItems = [
    { id: 'none', text: 'None' },
    ...columnsToSort.filter(({ id }) => !DEFAULT_ALERT_COLUMN_IDS.includes(id)),
  ];

  // The existing drilldown column
  const drilldownColumn = columnsState.find(column => column.hasDrilldown);

  const handleChangeSort = useCallback(
    ({ selectedItem }) => {
      const updatedColumnsState = [...columnsState];
      // Index of the previously selected column to sort by
      const oldSortColumnIndex = updatedColumnsState.findIndex(
        ({ sortDirection }) => sortDirection
      );
      // Need to remove the old selection
      if (oldSortColumnIndex > -1) {
        delete updatedColumnsState[oldSortColumnIndex].sortDirection;
      }
      // Index of the newly selected column to sort by
      const newSortColumnIndex = updatedColumnsState.findIndex(({ id }) => id === selectedItem.id);
      // Default the new column to ASC
      updatedColumnsState[newSortColumnIndex] = {
        ...updatedColumnsState[newSortColumnIndex],
        sortDirection: SortDirection.ASC,
      };
      onChange({ ...cardConfig, columnsState: updatedColumnsState });
    },
    [cardConfig, columnsState, onChange]
  );

  const handleChangeSortDirection = useCallback(
    direction => {
      const updatedColumnsState = [...columnsState];
      const sortByColumnIndex = updatedColumnsState.findIndex(({ sortDirection }) => sortDirection);
      if (sortByColumnIndex > -1) {
        updatedColumnsState[sortByColumnIndex] = {
          ...updatedColumnsState[sortByColumnIndex],
          sortDirection: direction,
        };
        onChange({ ...cardConfig, columnsState: updatedColumnsState });
      }
    },
    [cardConfig, columnsState, onChange]
  );

  const handleChangeDrilldown = useCallback(
    ({ selectedItem }) => {
      const updatedColumnsState = [...columnsState];
      // Index of the current (old) drilldown column
      const oldColumnDrilldownIndex = columnsState.findIndex(({ hasDrilldown }) => hasDrilldown);

      if (oldColumnDrilldownIndex > -1) {
        delete updatedColumnsState[oldColumnDrilldownIndex].hasDrilldown;
      }

      if (selectedItem.id !== 'none') {
        // Index to set hasDrilldown on
        const selectedColumnIndex = columnsState.findIndex(({ id }) => id === selectedItem.id);
        updatedColumnsState[selectedColumnIndex] = {
          ...updatedColumnsState[selectedColumnIndex],
          hasDrilldown: true,
        };
      }
      onChange({ ...cardConfig, columnsState: updatedColumnsState });
    },
    [cardConfig, columnsState, onChange]
  );

  return (
    <div className="alert-card-edit-settings">
      <div className="alert-card-edit-settings--rows">
        <div className="alert-card-edit-settings--rows-count">
          <Dropdown
            titleText={formatMessage(messages.rowsPerPage)}
            initialSelectedItem={customRowCount?.toString() || '10'}
            onChange={({ selectedItem }) =>
              onChange({ ...cardConfig, customRowCount: Number(selectedItem) })
            }
            label={formatMessage(messages.selectRowCount)}
            items={ALERT_ROW_COUNT_OPTIONS}
            translateWithId={id => formatMessage(messages[id])}
            light
          />
        </div>
        <FormGroup legendText={formatMessage(messages.rowHeight)}>
          <RadioButtonGroup
            name="row-height-radio-group"
            defaultSelected={customRowHeight || 'normal'}
            onChange={newRowHeight => onChange({ ...cardConfig, customRowHeight: newRowHeight })}>
            <RadioButton
              data-testid="edit-settings-row-height-normal"
              value="normal"
              labelText={formatMessage(messages.normal)}
            />
            <RadioButton
              data-testid="edit-settings-row-height-short"
              value="short"
              labelText={formatMessage(messages.short)}
            />
          </RadioButtonGroup>
        </FormGroup>
      </div>
      <div className="alert-card-edit-settings--sort">
        <Dropdown
          label={formatMessage(messages.selectSortColumn)}
          titleText={formatMessage(messages.sortBy)}
          selectedItem={
            sortByColumn.id
              ? { id: sortByColumn.id, text: sortByColumn.columnName }
              : columnsToSort[0]
          }
          itemToString={item => item.text}
          translateWithId={id => formatMessage(messages[id])}
          onChange={handleChangeSort}
          items={columnsToSort}
          light
        />
        <div className="alert-card-edit-settings--sort-controls">
          <Button
            onClick={() => handleChangeSortDirection(SortDirection.ASC)}
            selected={sortByColumn.sortDirection === SortDirection.ASC}
            hasIconOnly
            renderIcon={ArrowUp16}
            kind="icon-selection"
            size="field"
            iconDescription={formatMessage(messages.ascending)}
          />
          <Button
            onClick={() => handleChangeSortDirection(SortDirection.DESC)}
            selected={sortByColumn.sortDirection === SortDirection.DESC}
            hasIconOnly
            renderIcon={ArrowDown16}
            kind="icon-selection"
            size="field"
            iconDescription={formatMessage(messages.descending)}
          />
        </div>
      </div>
      {isSummaryDashboard && ( // Shouldn't be able to add a drill down in instance dashboards
        <div className="alert-card-edit-settings--drilldown">
          <TooltipDefinition tooltipText={formatMessage(messages.drilldownTooltip)}>
            {formatMessage(messages.tooltipDefinition)}
          </TooltipDefinition>
          <Dropdown
            itemToString={item => item.text}
            label={formatMessage(messages.selectAColumn)}
            initialSelectedItem={{
              id: drilldownColumn?.id || 'none',
              text: drilldownColumn?.columnName || formatMessage(messages.none),
            }}
            onChange={handleChangeDrilldown}
            translateWithId={id => formatMessage(messages[id])}
            items={drilldownItems}
            light
          />
        </div>
      )}
    </div>
  );
};

AlertCardEditSettings.propTypes = propTypes;
AlertCardEditSettings.defaultProps = defaultProps;

export default AlertCardEditSettings;
